import { mascarar, TAMANHO_MASCARADO, type TipoDeMascara } from '@aponte/shared';
import type { ReactNode } from 'react';

export interface CampoProps {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  placeholder?: string;
  tipo?: string;
  erro?: string;
  dica?: ReactNode;
  somenteLeitura?: boolean;
  /** Aplica máscara ao digitar e ao exibir. O valor entregue continua só com dígitos. */
  mascara?: TipoDeMascara;
  aoSairDoCampo?: (valor: string) => void;
  acao?: ReactNode;
}

/**
 * Campo de texto do cockpit, com máscara opcional.
 *
 * A máscara é aplicada na exibição e a cada tecla, mas **o que sobe para o `aoMudar` é o
 * valor sem pontuação**. Guardar CPF como "529.982.247-25" seria guardar decisão de
 * apresentação junto com o dado: bastaria alguém digitar sem ponto em outra tela para
 * criar dois valores para a mesma pessoa.
 *
 * A exceção é a UF, cuja "máscara" é o próprio dado — duas letras maiúsculas.
 */
/**
 * Sufixo estável por carregamento da página, usado no `name` dos campos.
 *
 * O Chrome decide o que preencher automaticamente pelas heurísticas de `name`, `id` e
 * rótulo. Num cockpit onde o operador digita o endereço **do candidato**, o preenchimento
 * automático enfia o endereço *dele próprio* na ficha alheia — foi assim que uma cidade
 * virou "12408". Com um `name` que não casa com nenhum padrão conhecido, a heurística não
 * dispara; o `autocomplete="off"` reforça, e o CSS neutraliza o resto.
 */
const SUFIXO_ANTI_AUTOPREENCHIMENTO = Math.random().toString(36).slice(2, 8);

export function Campo({
  rotulo,
  valor,
  aoMudar,
  placeholder,
  tipo,
  erro,
  dica,
  somenteLeitura,
  mascara,
  aoSairDoCampo,
  acao,
}: CampoProps) {
  const id = `campo-${rotulo.toLowerCase().replace(/\W+/g, '-')}`;
  const exibido = mascara ? mascarar(mascara, valor) : valor;

  return (
    <div className="campo">
      <label htmlFor={id}>{rotulo}</label>
      <div className={acao ? 'campo-com-acao' : undefined}>
        <input
          id={id}
          name={`${id}-${SUFIXO_ANTI_AUTOPREENCHIMENTO}`}
          autoComplete="off"
          data-lpignore="true"
          data-form-type="other"
          type={tipo ?? 'text'}
          value={exibido}
          placeholder={placeholder}
          disabled={somenteLeitura}
          inputMode={mascara && mascara !== 'uf' ? 'numeric' : undefined}
          maxLength={mascara ? TAMANHO_MASCARADO[mascara] : undefined}
          onChange={(e) => {
            const bruto = e.target.value;
            if (!mascara) {
              aoMudar(bruto);
              return;
            }
            // A máscara normaliza; o domínio recebe o dado limpo.
            aoMudar(mascara === 'uf' ? mascarar('uf', bruto) : bruto.replace(/\D/g, ''));
          }}
          onBlur={(e) => aoSairDoCampo?.(e.target.value)}
        />
        {acao}
      </div>
      {erro ? (
        <div className="erro-campo">{erro}</div>
      ) : dica ? (
        <div className="dica">{dica}</div>
      ) : null}
    </div>
  );
}
